import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Sparkles, KeyRound, CheckCircle2, XCircle, Loader2, ExternalLink } from "lucide-react";

const PROVIDERS = [
  {
    id: "emergent",
    label_ar: "Emergent (المفتاح العالمي)",
    label_en: "Emergent (Universal Key)",
    hint_ar: "المفتاح العالمي من Emergent — يدعم Claude/OpenAI/Gemini بمفتاح واحد. اتركه فارغاً لاستخدام المفتاح المُعرّف على الخادم.",
    hint_en: "Emergent's universal key — supports Claude/OpenAI/Gemini with one key. Leave empty to use the server-installed key.",
    key_needed: false,  // has server fallback
    key_url: "",
    default_model: "claude-sonnet-4-6",
    models: ["claude-sonnet-4-6", "claude-opus-4-8", "gpt-5.4", "gemini-3.1-pro-preview"],
  },
  {
    id: "openai",
    label_ar: "OpenAI (ChatGPT)",
    label_en: "OpenAI (ChatGPT)",
    hint_ar: "استخدم مفتاحك من OpenAI. يدعم GPT-5.4 و o3.",
    hint_en: "Use your OpenAI key. Supports GPT-5.4 and o3.",
    key_needed: true,
    key_url: "https://platform.openai.com/api-keys",
    default_model: "gpt-5.4",
    models: ["gpt-5.5", "gpt-5.4", "gpt-5.4-mini", "gpt-5.2", "gpt-4o", "gpt-4.1", "o3", "o4-mini"],
  },
  {
    id: "anthropic",
    label_ar: "Anthropic Claude",
    label_en: "Anthropic Claude",
    hint_ar: "مفتاح Anthropic. Claude Sonnet 4.6 هو الموصى به.",
    hint_en: "Anthropic key. Claude Sonnet 4.6 recommended.",
    key_needed: true,
    key_url: "https://console.anthropic.com/settings/keys",
    default_model: "claude-sonnet-4-6",
    models: ["claude-opus-4-8", "claude-opus-4-7", "claude-sonnet-4-6", "claude-opus-4-6", "claude-sonnet-4-5-20250929", "claude-haiku-4-5-20251001"],
  },
  {
    id: "gemini",
    label_ar: "Google Gemini",
    label_en: "Google Gemini",
    hint_ar: "مفتاح Google AI Studio. Gemini 3.1 Pro Preview هو الموصى به.",
    hint_en: "Google AI Studio key. Gemini 3.1 Pro Preview recommended.",
    key_needed: true,
    key_url: "https://aistudio.google.com/api-keys",
    default_model: "gemini-3.1-pro-preview",
    models: ["gemini-3.5-flash", "gemini-3.1-pro-preview", "gemini-3-flash-preview", "gemini-2.5-pro", "gemini-2.5-flash"],
  },
];

const EMPTY_KEYS = { emergent: "", openai: "", anthropic: "", gemini: "" };

export default function AiProviderCard({ schoolId }) {
  const { t, lang } = useI18n();
  const [keysStatus, setKeysStatus] = useState({}); // {provider: {has_key, hint}}
  const [provider, setProvider] = useState("emergent");
  const [model, setModel] = useState("");
  const [apiKeys, setApiKeys] = useState(EMPTY_KEYS); // typed-but-not-saved keys per provider
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = () => {
    if (!schoolId) return;
    api.get(`/ai/settings?school_id=${schoolId}`).then(r => {
      setKeysStatus(r.data.keys || {});
      setProvider(r.data.ai_provider || "emergent");
      setModel(r.data.ai_model || "");
      setApiKeys(EMPTY_KEYS); // never prefill actual keys
    }).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [schoolId]);

  const activeProvider = PROVIDERS.find(p => p.id === provider) || PROVIDERS[0];
  const currentTypedKey = apiKeys[provider] || "";
  const currentStoredKey = keysStatus[provider] || { has_key: false, hint: "" };
  const isEmergent = provider === "emergent";
  const keyRequired = activeProvider.key_needed && !currentStoredKey.has_key && !currentTypedKey;

  const save = async () => {
    if (!schoolId) { toast.error(t("select_school") || "Select a school first"); return; }
    setSaving(true);
    try {
      // Only send keys the user typed (empty = don't touch that provider)
      const keysToSend = {};
      for (const p of Object.keys(apiKeys)) {
        if (apiKeys[p] !== "") keysToSend[p] = apiKeys[p];
      }
      const payload = {
        school_id: schoolId,
        ai_provider: provider,
        ai_model: model || null,
      };
      if (Object.keys(keysToSend).length > 0) payload.ai_keys = keysToSend;
      await api.put("/ai/settings", payload);
      toast.success(t("ai_settings_saved") || "AI settings saved");
      setApiKeys(EMPTY_KEYS);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const testKey = async () => {
    setTesting(true); setTestResult(null);
    try {
      const payload = { provider, model: model || null };
      if (currentTypedKey) payload.api_key = currentTypedKey;
      const { data } = await api.post("/ai/settings/test", payload);
      setTestResult(data);
      if (data.ok) toast.success(t("ai_test_ok") || "Connection successful");
      else toast.error((t("ai_test_fail") || "Test failed") + `: ${data.hint || data.detail}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setTesting(false); }
  };

  const clearKey = async () => {
    if (!window.confirm(t("ai_clear_key_confirm") || "Clear stored API key for this provider?")) return;
    try {
      await api.put("/ai/settings", { school_id: schoolId, ai_keys: { [provider]: "" } });
      toast.success(t("ai_key_cleared") || "API key cleared");
      setApiKeys(EMPTY_KEYS); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  // Which providers already have a saved key (green dot)
  const savedProviders = Object.entries(keysStatus).filter(([, v]) => v.has_key).map(([p]) => p);

  return (
    <Card className="p-6 border shadow-none space-y-6">
      <div className="flex items-start gap-3 pb-2">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 grid place-items-center text-white shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-semibold">{t("ai_provider_settings") || "AI Provider Settings"}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{t("ai_provider_hint") || "Each school can use its own provider — and each provider keeps its own API key."}</p>
          {savedProviders.length > 0 && (
            <p className="text-[11px] text-emerald-600 mt-1">
              ✓ {t("ai_keys_configured") || "Keys stored for"}: {savedProviders.join(", ")}
            </p>
          )}
        </div>
      </div>

      {/* Provider grid */}
      <div>
        <Label className="mb-2 block">{t("ai_provider") || "AI Provider"}</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {PROVIDERS.map(p => {
            const stored = keysStatus[p.id];
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => { setProvider(p.id); setModel(p.default_model); setTestResult(null); }}
                className={`text-start p-3 rounded-xl border-2 transition-all relative ${provider === p.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                data-testid={`ai-provider-${p.id}`}
              >
                {stored?.has_key && (
                  <span className="absolute top-2 end-2 w-2 h-2 rounded-full bg-emerald-500" title={`Key stored (••${stored.hint})`} />
                )}
                <div className="font-semibold text-sm">{lang === "ar" ? p.label_ar : p.label_en}</div>
                <div className="text-[10px] text-muted-foreground mt-1 line-clamp-2">{lang === "ar" ? p.hint_ar : p.hint_en}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Model + API Key for THIS provider */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="mb-1 block">{t("ai_model") || "Model"}</Label>
          <Select value={model || activeProvider.default_model} onValueChange={setModel}>
            <SelectTrigger data-testid="ai-model-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {activeProvider.models.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 flex items-center justify-between">
            <span>{t("ai_api_key") || "API Key"} · <span className="text-muted-foreground text-[11px]">{activeProvider[`label_${lang}`] || activeProvider.label_en}</span></span>
            {activeProvider.key_url && (
              <a href={activeProvider.key_url} target="_blank" rel="noreferrer"
                 className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />{t("ai_get_key") || "Get key"}
              </a>
            )}
          </Label>
          <div className="flex gap-2">
            <Input
              type="password"
              placeholder={currentStoredKey.has_key ? `••••••••${currentStoredKey.hint}` : (isEmergent ? "sk-emergent-... (اختياري)" : "sk-...")}
              value={currentTypedKey}
              onChange={e => setApiKeys({ ...apiKeys, [provider]: e.target.value })}
              data-testid="ai-api-key-input"
            />
            {currentStoredKey.has_key && (
              <Button type="button" variant="ghost" onClick={clearKey} data-testid="ai-api-key-clear" title={t("ai_clear_key_confirm")}>
                <XCircle className="w-4 h-4" />
              </Button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            {isEmergent
              ? (currentStoredKey.has_key
                  ? (t("ai_key_stored") || "Custom Emergent key stored. Leave field empty to keep it, or type a new one.")
                  : (t("ai_emergent_default") || "No custom key — will use the server-installed EMERGENT_LLM_KEY."))
              : (currentStoredKey.has_key
                  ? (t("ai_key_stored") || "A key is stored. Leave empty to keep it, or type a new one.")
                  : (t("ai_key_missing") || "No key stored. Paste your API key to enable this provider."))}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 items-center border-t border-border pt-4">
        <Button onClick={save} disabled={saving} data-testid="ai-save-btn">
          {saving && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
          <KeyRound className="w-4 h-4 me-2" />
          {t("save") || "Save"}
        </Button>
        <Button onClick={testKey} disabled={testing || keyRequired} variant="outline" data-testid="ai-test-btn">
          {testing ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 me-2" />}
          {t("ai_test_connection") || "Test Connection"}
        </Button>
        {keyRequired && (
          <span className="text-xs text-amber-600">{t("ai_need_key_first") || "Add API key first to test"}</span>
        )}
        {testResult && (
          <div className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${testResult.ok ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "bg-rose-500/15 text-rose-700 dark:text-rose-400"}`}>
            {testResult.ok
              ? <><CheckCircle2 className="w-3.5 h-3.5" /> {testResult.model}</>
              : <><XCircle className="w-3.5 h-3.5" /> {testResult.hint || "Failed"}</>}
          </div>
        )}
      </div>
    </Card>
  );
}
